import { ComponentProps, FC, ReactElement, useCallback, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { TypeOfDrawer, TypeOfDrawerEnum } from '@/_shared/types';
import { Claims } from '../../constants';
import { AlertBadge } from '../AlertBadge';
import { CtaContainer } from '../CtaContainer';
import { useDrawer } from '../Drawer';
import { InternalDrawer } from '../InternalDrawer';
import { PaginatedTable } from './PaginatedTable';

export type StyledTableDrawer = ReturnType<typeof useDrawer> & {
  instances: {
    type: TypeOfDrawer;
    component: JSX.Element;
    claimKey?: Claims;
    customLabel?: string;
    drawerProps?: ComponentProps<typeof InternalDrawer>['drawerProps'];
  }[];
};

type Props = {
  drawer: StyledTableDrawer;
  error?: boolean;
  errorMessage?: string;
  errorType?: 'error' | 'success' | 'warning';
  loading?: boolean;
  onRefreshData?: ComponentProps<typeof CtaContainer>['onRefreshData'];
  children: ReactElement<ComponentProps<typeof PaginatedTable>>;
  hasElement?: boolean;
};

export const StyledTable: FC<Props> = ({
  drawer,
  error,
  errorMessage,
  errorType = 'error',
  loading,
  onRefreshData,
  children,
  hasElement,
}) => {
  const t = useTranslations();
  const { isOpen, open, Drawer, close } = drawer;
  const [type, setType] = useState<TypeOfDrawer>();

  const openDrawer = useCallback(
    (internalType: TypeOfDrawer) => {
      setType(internalType);
      open();
    },
    [open],
  );

  const helperOpenDrawer = useCallback(
    (
      key: TypeOfDrawer,
    ): { callback: () => void; claimKey?: Claims; customLabel: string } | undefined => {
      const drawerInstance = drawer.instances.find(el => el.type === key);
      if (drawerInstance) {
        return {
          callback: () => openDrawer(key),
          claimKey: drawerInstance.claimKey,
          customLabel: drawerInstance.customLabel || '',
        };
      }
      return undefined;
    },
    [drawer.instances, openDrawer],
  );

  if (error) {
    const message = errorMessage || t('errors.genericApiError');
    return <AlertBadge iconName='info' type={errorType} label={message} />;
  }

  return (
    <Box display='flex' flexDirection='column'>
      <Box
        display='flex'
        justifyContent='space-between'
        width='100%'
        flexDirection='column'
        alignItems='flex-end'
      >
        {!error && !loading && (
          <Box mb='xs'>
            <CtaContainer
              onRefreshData={onRefreshData}
              onViewDetail={helperOpenDrawer(TypeOfDrawerEnum.VIEW)?.callback}
              onFilter={helperOpenDrawer(TypeOfDrawerEnum.FILTER)?.callback}
              onAdd={helperOpenDrawer(TypeOfDrawerEnum.CREATE)?.callback}
              onAddClaimKey={helperOpenDrawer(TypeOfDrawerEnum.CREATE)?.claimKey}
              onEdit={helperOpenDrawer(TypeOfDrawerEnum.MODIFY)?.callback}
              onEditClaimKey={helperOpenDrawer(TypeOfDrawerEnum.MODIFY)?.claimKey}
              onClose={helperOpenDrawer(TypeOfDrawerEnum.CLOSE)?.callback}
              onCloseClaimKey={helperOpenDrawer(TypeOfDrawerEnum.CLOSE)?.claimKey}
              onCustom={helperOpenDrawer(TypeOfDrawerEnum.CUSTOM)?.callback}
              onCustomLabel={helperOpenDrawer(TypeOfDrawerEnum.CUSTOM)?.customLabel}
              onCustomClaimKey={helperOpenDrawer(TypeOfDrawerEnum.CUSTOM)?.claimKey}
              hasElement={hasElement}
            />
          </Box>
        )}
      </Box>
      {children}
      {drawer.instances.map(drawerInstance => {
        if (drawerInstance.type === type) {
          return (
            <InternalDrawer
              key={`${drawerInstance.type}`}
              Drawer={Drawer}
              isOpen={isOpen}
              drawerProps={drawerInstance.drawerProps || undefined}
              onClose={close}
            >
              {drawerInstance.component}
            </InternalDrawer>
          );
        }
        return null;
      })}
    </Box>
  );
};
